import { useSyncExternalStore } from 'react';

/**
 * Tiny shared store: a line marks its key when it starts drawing, and the
 * card it points at waits for that key before fading in. Keeps branch -> card
 * choreography deterministic without passing timers around.
 */
const revealed = new Set<string>();
const listeners = new Set<() => void>();

export function markRevealed(key: string) {
  if (revealed.has(key)) return;
  revealed.add(key);
  listeners.forEach((listener) => listener());
}

/** Synchronous check, for seeding initial state (e.g. a line that remounts after a resize). */
export function isRevealed(key?: string): boolean {
  return key === undefined || revealed.has(key);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True once `key` has been marked. An undefined key is always revealed. */
export function useRevealed(key?: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isRevealed(key),
    () => key === undefined
  );
}
